import { useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import Loading from 'common/loading';
import { IPromotion } from 'common/promotion';
import { keyValueArrayToHashMap } from 'common/travelLabel/utils';
import { IItinerary, IKeyStringValue } from 'common/types';
import { DeviceContext, DeviceType } from 'contexts/device';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import { TripType } from 'contexts/searchParams';
import { FareTarget, IFareDetail, ISchedule } from 'list/international/types';
import { getPriceOfPerson, hasAdultNaverFare } from 'list/international/utils';
import uniq from 'lodash/uniq';
import { Dispatch, SetStateAction, useContext } from 'react';
import GET_BOOKING_PARTNERS from '../common/travelLabel/bookingPartners.gql';
import AdsAirline from './adsAirline';
import BookingOption from './bookingOption';
import styles from './index.module.scss';
import NoAgentResult from './noAgentResult';
import GET_OTA_LIST from './otaList.gql';
import { IBookingOptions, IOta, IPassengerCount } from './types';
import {
  areaCode,
  getShowAgentList,
  getTotalPrice,
  mergeOtaFareList,
} from './utils';

const cx = classNames.bind(styles);

const getOtaListParams = (
  trip: string,
  seatClass: string,
  fareDataList: IFareDetail[],
  selectedScheduleIds: string[],
  selectedSchedulesData: ISchedule[],
  passengerCount: IPassengerCount,
  device: DeviceType,
  selectedLabels: string[][],
) => {
  const { adultCnt, childCnt, infantCnt } = passengerCount;
  return {
    searchParams: {
      itinerary: selectedSchedulesData.map(({ detail }) => ({
        departureAirport: detail[0].sa,
        arrivalAirport: detail[detail.length - 1].ea,
        departureDate: detail[0].sdt.slice(0, 8),
      })),
      person: { adult: adultCnt, child: childCnt, infant: infantCnt },
      trip,
      fareType: seatClass,
    },
    otaFares: fareDataList?.map((item: IFareDetail) => ({
      otaId: item.AgtCode,
      fare: getTotalPrice(item, adultCnt, childCnt, infantCnt, true),
      fareType: item.FareType,
      bookingUrl: item.ReserveParameter['#cdata-section'],
      bookingParams: selectedSchedulesData.map(({ detail }) => ({
        airlineCode: detail.every(({ av }) => av === detail[0].av)
          ? detail[0].av
          : 'multiple',
        fitName: detail[0].fn,
        depCity: detail[0].sa,
        arrCity: detail[detail.length - 1].ea,
        departureDateTime: detail[0].sdt,
        arrivalDateTime: detail[detail.length - 1].edt,
        seatClass,
        etc: '',
        stops: detail.length - 1,
        adultFare: getPriceOfPerson(item.Adult, hasAdultNaverFare(item)),
        childFare: getPriceOfPerson(item.Child, hasAdultNaverFare(item)),
        infantFare: getPriceOfPerson(item.Infant, hasAdultNaverFare(item)),
        aFuel: +item[FareTarget.Adult].QCharge,
        cFuel: +item[FareTarget.Child].QCharge,
        iFuel: +item[FareTarget.Infant].QCharge,
        aTax: +item[FareTarget.Adult].Tax,
        cTax: +item[FareTarget.Child].Tax,
        iTax: +item[FareTarget.Infant].Tax,
      })),
    })),
    device,
    scheduleIds: selectedScheduleIds,
    curations: uniq(selectedLabels.flat()),
  };
};

interface IAgentListProps {
  fareDataList: IFareDetail[];
  isMore: boolean;
  itineraryList: IItinerary[];
  korFareTypes: IKeyStringValue;
  loaded: boolean;
  passengerCount: IPassengerCount;
  promotions: IPromotion[];
  refetchRef: React.MutableRefObject<() => void>;
  seatClass: string;
  selectedLabels: string[][];
  selectedScheduleIds: string[];
  selectedSchedulesData: ISchedule[];
  trip: TripType;
  handleBooking: ({ otaId, agreeStatus, bookingUrl }: IOta) => () => void;
  setIsMore: Dispatch<SetStateAction<boolean>>;
  setMinFare: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const AgentList = ({
  fareDataList,
  isMore,
  itineraryList,
  korFareTypes,
  loaded,
  passengerCount,
  passengerCount: { adultCnt, childCnt, infantCnt },
  promotions,
  refetchRef,
  seatClass,
  selectedLabels,
  selectedScheduleIds,
  selectedSchedulesData,
  trip,
  handleBooking,
  setIsMore,
  setMinFare,
}: IAgentListProps) => {
  const device = useContext(DeviceContext);
  const { withReloadTimer } = useReloadTimerContext();
  const {
    data: bookingPartnerData,
    loading: bookingPartnerLoading,
    error: bookingPartnerError,
  } = useQuery(GET_BOOKING_PARTNERS);

  const otaParams = getOtaListParams(
    trip,
    seatClass,
    fareDataList,
    selectedScheduleIds,
    selectedSchedulesData,
    passengerCount,
    device,
    selectedLabels,
  );
  const firstShowCount: number = 5;

  const { data, error, loading, previousData, refetch } =
    useQuery<IBookingOptions>(GET_OTA_LIST, {
      variables: otaParams,
      skip: !fareDataList || (fareDataList && fareDataList.length === 0),
      onCompleted: () => {
        // eslint-disable-next-line no-param-reassign
        refetchRef.current = refetch;
      },
    });

  const cachedData = data || previousData;
  const otaList = cachedData?.bookingOptions?.otaList;

  if (loading && !cachedData) {
    return <Loading />;
  }

  if (
    (error ||
      !cachedData ||
      (loaded && !otaList?.length) ||
      bookingPartnerLoading ||
      bookingPartnerError) &&
    loaded
  ) {
    return <NoAgentResult />;
  }

  const adExposureOtaList = otaList?.filter(ota => ota?.active?.ad) ?? [];
  const otaFareList = otaList?.filter(ota => ota?.active?.realtime) ?? [];

  const bookingPartnersHashMap = keyValueArrayToHashMap(
    bookingPartnerData?.bookingPartners,
    'otaId',
    'expName',
  );

  const adList = mergeOtaFareList(adExposureOtaList, fareDataList);
  const mergeList = mergeOtaFareList(otaFareList, fareDataList); // 같은건 머지
  const agentList = isMore
    ? mergeList
    : getShowAgentList(
        mergeList,
        firstShowCount,
        adultCnt,
        childCnt,
        infantCnt,
      ); // 마지막 아이템과 같은 가격이 있으면 추가

  const checkOne = fareDataList.every(
    (item: any) => fareDataList[0].FareType === item.FareType,
  );

  const departureDate = itineraryList[0].departureDate;

  const onClickBooking = (ota: IOta) => withReloadTimer(handleBooking(ota));

  const selectedScheduleDetails = selectedSchedulesData.map(
    ({ detail }) => detail,
  );

  return (
    <>
      <AdsAirline
        adList={adList}
        passengerCount={passengerCount}
        onClickBooking={onClickBooking}
      />
      <div className={cx('AgentList')}>
        {agentList.map(({ ota, fare }, idx: number) => (
          <BookingOption
            key={idx}
            bookingPartnersHashMap={bookingPartnersHashMap}
            checkOne={checkOne}
            departureDate={departureDate}
            fare={fare}
            idx={idx}
            korFareTypes={korFareTypes}
            ota={ota}
            passengerCount={passengerCount}
            promotions={promotions}
            selectedScheduleDetails={selectedScheduleDetails}
            onClickBooking={onClickBooking}
            setMinFare={setMinFare}
          />
        ))}
        {!loaded && (
          <div
            className={cx('list_loading_area')}
            aria-label="Loading..."
            aria-busy="true"
          />
        )}
        {mergeList.length > agentList.length && (
          <button
            type="button"
            className={cx('more')}
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.moreota`}
            onClick={() => {
              setIsMore(true);
            }}
          >
            <b className={cx('count')}>{mergeList.length - agentList.length}</b>
            <span className={cx('unit')}>개</span>
            <span className={cx('txt')}>판매사 더보기</span>
          </button>
        )}
      </div>
    </>
  );
};

export default AgentList;
